import React, { useEffect, useRef, useState } from "react";
import "./ShaftManholePipeJacking.css";

// Gallery image imports — replace with your actual image files
import gallery1 from "../assets/gallery/sm-gallery-1.jpeg";
import gallery2 from "../assets/gallery/sm-gallery-2.jpeg";
import gallery3 from "../assets/gallery/sm-gallery-3.jpeg";
import gallery4 from "../assets/gallery/sm-gallery-4.jpeg";
import gallery5 from "../assets/gallery/sm-gallery-5.jpeg";
import gallery6 from "../assets/gallery/sm-gallery-6.jpeg";
import gallery7 from "../assets/gallery/sm-gallery-7.jpeg";
import gallery8 from "../assets/gallery/sm-gallery-8.jpeg";
import overviewImg from "../assets/gallery/sm-gallery-1.jpeg";

const GALLERY_IMAGES = [
  { src: gallery1},
  { src: gallery2},
  { src: gallery3},
  { src: gallery4},
  { src: gallery5},
  { src: gallery6},
  { src: gallery7},
  { src: gallery8},
];

/* ── Main ShaftManholePipeJacking Component ── */
const ShaftManholePipeJacking = () => {
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    const observers = {};
    Object.keys(sectionRefs.current).forEach((key) => {
      observers[key] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [key]: true }));
          }
        },
        { threshold: 0.15 }
      );
      if (sectionRefs.current[key]) {
        observers[key].observe(sectionRefs.current[key]);
      }
    });
    return () => {
      Object.values(observers).forEach((obs) => obs.disconnect());
    };
  }, []);

  const setRef = (key) => (el) => {
    sectionRefs.current[key] = el;
  };

  const methodsData = [
    {
      number: "01",
      title: "Shaft Excavation",
      desc: "Caisson sinking or secant/sheet pile shaft construction to design depth, providing safe access for jacking and tunnelling operations.",
      icon: "⬇",
    },
    {
      number: "02",
      title: "Manhole Construction",
      desc: "Precast or cast-in-situ manhole chambers constructed for sewer, drainage, and utility access points to specification.",
      icon: "🏗",
    },
    {
      number: "03",
      title: "Pipe Jacking",
      desc: "Hydraulic jacking of concrete or steel pipes through the ground from a launch shaft to a reception shaft, minimising surface disruption.",
      icon: "🔧",
    },
    {
      number: "04",
      title: "Microtunnelling",
      desc: "Remote-controlled guided boring for precise line and level control in pipe jacking through variable ground and groundwater conditions.",
      icon: "🌀",
    },
    {
      number: "05",
      title: "Shaft Lining & Waterproofing",
      desc: "Reinforced concrete lining and waterproofing membrane installation to ensure shaft structural integrity and watertightness.",
      icon: "🧱",
    },
    {
      number: "06",
      title: "Survey, QA/QC & Reporting",
      desc: "Continuous line and level survey control with full quality assurance documentation and as-built reporting upon completion.",
      icon: "📋",
    },
  ];

  return (
    <div className="sm-page" id="#pipe-jacking">
      {/* Top Banner */}
      <section className="sm-top-banner">
        <div className="sm-top-banner__grid" />
        <div className="sm-top-banner__glow" />
        <h1 className="sm-top-banner__title">
          Pipe<span className="sm-text-accent"> Jacking</span>
        </h1>
      </section>

      {/* Overview Section */}
      <section
        className={`sm-overview ${isVisible["overview"] ? "sm-animate-in" : ""}`}
        ref={setRef("overview")}
      >
        <div className="sm-overview__inner">
          <div className="sm-overview__left">
            <div className="sm-section-label">
              <span className="sm-section-label__line" />
              WHAT WE DO
            </div>
            <h2 className="sm-section-title">
              BUILDING BELOW
              <br />
              <span className="sm-text-accent">WITHOUT DIGGING UP</span>
            </h2>
            <p className="sm-overview__text">
              Trenchless utility installation demands precision shaft and pipeline works.
              GSK Geotech's Shaft, Manhole Construction & Pipe Jacking service delivers the
              full sequence — from launch and reception shaft construction to manhole
              chambers and hydraulically jacked pipelines — with minimal surface disruption.
            </p>
            <p className="sm-overview__text">
              Our experienced team has executed shaft and pipe jacking works across major
              sewer, drainage, and utility infrastructure projects, delivering accurate
              line and level control backed by full QA/QC documentation.
            </p>
            <div className="sm-overview__highlights">
              {[
                "Experienced shaft & pipe jacking specialists",
                "Caisson, secant & sheet pile shaft methods",
                "End-to-end: shaft, jacking, lining, reporting",
                "Compliant with industry & regulatory standards",
              ].map((item, i) => (
                <div key={i} className="sm-overview__highlight-item">
                  <span className="sm-overview__highlight-icon">✦</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className="sm-overview__right">
            <div className="sm-overview__image-block">
              <img
                src={overviewImg}
                alt="Shaft Manhole Construction and Pipe Jacking Works"
                className="sm-overview__image"
              />
              <div className="sm-overview__stat-card">
                <span className="sm-overview__stat-number">2+</span>
                <span className="sm-overview__stat-text">JACKING<br />PROJECTS</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methods Section */}
      <section
        className={`sm-methods ${isVisible["methods"] ? "sm-animate-in" : ""}`}
        ref={setRef("methods")}
      >
        <div className="sm-methods__inner">
          <div className="sm-section-label">
            <span className="sm-section-label__line" />
            OUR APPROACH
          </div>
          <h2 className="sm-section-title">
            CONSTRUCTION
            <br />
            <span className="sm-text-accent">METHODS</span>
          </h2>
          <p className="sm-methods__subtitle">
            From shaft sinking to microtunnelling — we deploy a full suite of trenchless
            construction techniques tailored to each site's ground conditions.
          </p>
          <div className="sm-methods__grid">
            {methodsData.map((method, i) => (
              <div
                key={i}
                className="sm-method-card"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="sm-method-card__number">{method.number}</div>
                <div className="sm-method-card__icon">{method.icon}</div>
                <h3 className="sm-method-card__title">{method.title}</h3>
                <p className="sm-method-card__desc">{method.desc}</p>
                <div className="sm-method-card__line" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section
        className={`sm-process ${isVisible["process"] ? "sm-animate-in" : ""}`}
        ref={setRef("process")}
      >
        <div className="sm-process__inner">
          <div className="sm-section-label">
            <span className="sm-section-label__line" />
            HOW WE WORK
          </div>
          <h2 className="sm-section-title">
            OUR <span className="sm-text-accent">PROCESS</span>
          </h2>
          <div className="sm-process__steps">
            {[
              { step: "01", title: "Site Mobilisation", desc: "Jacking rig setup, shaft excavation equipment positioning, safety checks and permit acquisition prior to works." },
              { step: "02", title: "Launch & Reception Shaft Construction", desc: "Excavation and lining of launch and reception shafts to design depth with thrust wall installation." },
              { step: "03", title: "Pipe Jacking Operations", desc: "Hydraulic jacking of pipe sections from the launch shaft, guided by continuous line and level survey control." },
              { step: "04", title: "Manhole Construction", desc: "Construction of manhole chambers at shaft locations to connect and access the completed pipeline." },
              { step: "05", title: "Survey, QA/QC & Handover", desc: "Final as-built survey, quality verification, and full documentation handover to the client." },
            ].map((item, i) => (
              <div key={i} className="sm-process__step">
                <div className="sm-process__step-num">{item.step}</div>
                <div className="sm-process__step-connector" />
                <div className="sm-process__step-content">
                  <h4 className="sm-process__step-title">{item.title}</h4>
                  <p className="sm-process__step-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section
        className={`sm-projects ${isVisible["projects"] ? "sm-animate-in" : ""}`}
        ref={setRef("projects")}
      >
        <div className="sm-projects__inner">
          <div className="sm-section-label">
            <span className="sm-section-label__line" />
            TRACK RECORD
          </div>
          <h2 className="sm-section-title">
            COMPLETED SHAFT &
            <br />
            <span className="sm-text-accent">PIPE JACKING PROJECTS</span>
          </h2>
          <div className="sm-projects__grid">
            {[
              { title: "Pipe Jacking Work at Pulau Punggol Barat", client: "ASK M&E PTE LTD" },
            ].map((proj, i) => (
              <div key={i} className="sm-project-card">
                <h4 className="sm-project-card__title">{proj.title}</h4>
                <p className="sm-project-card__client">{proj.client}</p>
              </div>
            ))}
          </div>
          <div className="sm-projects__cta">
            <a href="/projects" className="sm-btn sm-btn--outline">
              VIEW ALL PROJECTS →
            </a>
          </div>
        </div>
      </section>

      

      {/* CTA Banner */}
      <section className="sm-cta">
        <div className="sm-cta__inner">
          <div className="sm-cta__left">
            <p className="sm-cta__label">READY TO START?</p>
            <h2 className="sm-cta__title">
              PIPE
              <br />
              <span className="sm-text-accent">JACKING WORKS?</span>
            </h2>
            <p className="sm-cta__desc">
              Our team is ready to mobilise. Get in touch for a detailed quote
              and consultation tailored to your project's trenchless installation requirements.
            </p>
          </div>
          <div className="sm-cta__right">
            <a href="/contact-us" className="sm-btn sm-btn--primary sm-btn--large">
              CONTACT US →
            </a>
            <div className="sm-cta__contacts">
              <span>Nedu: +65 9239 8494</span>
              <span>Suben: +65 9027 1160</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShaftManholePipeJacking;